/**
 * Single fetch wrapper for the hub API.
 *
 * The browser authenticates with the session cookie (same-origin, so it rides
 * along automatically). API keys and Basic auth exist for REST clients and are
 * not used here.
 */

export class ApiError extends Error {
  readonly status: number;
  /** Set when the server flagged the failure as a config-validation problem. */
  readonly validation?: boolean;
  /** Set when the failure was a missing OS privilege. */
  readonly permission?: boolean;

  constructor(message: string, status: number, extra?: { validation?: boolean; permission?: boolean }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.validation = extra?.validation;
    this.permission = extra?.permission;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, headers: {}, credentials: 'same-origin' };
  if (body !== undefined) {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(path.startsWith('/') ? path : `/${path}`, init);

  // 204 and empty bodies are valid successes
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = null; }
  }

  if (!res.ok) {
    const payload = (parsed ?? {}) as { error?: string; validation?: boolean; permission?: boolean };
    throw new ApiError(payload.error || `HTTP ${res.status}`, res.status, {
      validation: payload.validation,
      permission: payload.permission,
    });
  }
  return (parsed ?? {}) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
